// Client-side reading tools for a Meeting Reviews call's Transcript tab: search
// with <mark> highlights and match stepping, a "matches only" filter, and
// clicking a speaker card to toggle highlighting their turns.
import { Controller } from '@hotwired/stimulus'

const MIN_QUERY = 2
const ACTIVE_MATCH = ['bg-warning']
const DIMMED = 'opacity-50'
const INTERACTIVE = 'select, input, a, button, label, [data-bs-toggle]'

export default class extends Controller {
  static targets = ['query', 'position', 'turn', 'text', 'divider', 'speaker', 'matchesOnly']

  connect() {
    this.matches = []
    this.index = -1
    this.matchesOnly = false
    this.focused = null
  }

  search() {
    this.clearMarks()
    const query = this.queryTarget.value.trim()
    if (query.length >= MIN_QUERY) this.textTargets.forEach((text) => this.mark(text, query.toLowerCase()))
    this.index = -1
    this.applyMatchesOnly()
    this.matches.length ? this.go(0) : this.renderPosition()
  }

  step(event) {
    if (event.key === 'Escape') {
      this.queryTarget.value = ''
      this.search()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      event.shiftKey ? this.previous() : this.next()
    }
  }

  next() {
    if (this.matches.length) this.go((this.index + 1) % this.matches.length)
  }

  previous() {
    if (this.matches.length) this.go((this.index - 1 + this.matches.length) % this.matches.length)
  }

  toggleMatches() {
    this.matchesOnly = !this.matchesOnly
    this.matchesOnlyTarget.classList.toggle('active', this.matchesOnly)
    this.matchesOnlyTarget.setAttribute('aria-pressed', this.matchesOnly)
    this.applyMatchesOnly()
  }

  // The role dropdown and info popover live inside the card; using them
  // shouldn't toggle the highlight.
  toggleSpeaker(event) {
    if (event.target.closest(INTERACTIVE)) return

    event.preventDefault()
    const speaker = event.currentTarget.dataset.speaker
    const wasFocused = speaker === this.focused
    this.clearFocus()
    if (wasFocused) return

    this.focused = speaker
    this.highlightSpeaker(speaker)
    this.turnTargets
      .find((turn) => turn.dataset.speaker === speaker && !turn.classList.contains('d-none'))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  // private

  clearFocus() {
    if (this.focused === null) return

    const highlight = this.highlightFor(this.focused)
    this.turnTargets.forEach((turn) => turn.classList.remove(highlight, DIMMED))
    this.speakerTargets.forEach((card) => {
      card.classList.remove(this.highlightFor(card.dataset.speaker), DIMMED)
      card.setAttribute('aria-pressed', 'false')
    })
    this.focused = null
  }

  highlightSpeaker(speaker) {
    const highlight = this.highlightFor(speaker)
    this.turnTargets.forEach((turn) => {
      const theirs = turn.dataset.speaker === speaker
      turn.classList.toggle(highlight, theirs)
      turn.classList.toggle(DIMMED, !theirs)
    })
    this.speakerTargets.forEach((card) => {
      const theirs = card.dataset.speaker === speaker
      card.classList.toggle(highlight, theirs)
      card.classList.toggle(DIMMED, !theirs)
      card.setAttribute('aria-pressed', theirs)
    })
  }

  highlightFor(speaker) {
    const card = this.speakerTargets.find((target) => target.dataset.speaker === speaker)
    return card?.dataset.highlight || 'bg-light'
  }

  go(index) {
    this.matches[this.index]?.classList.remove(...ACTIVE_MATCH)
    this.index = index
    const match = this.matches[index]
    match.classList.add(...ACTIVE_MATCH)
    match.scrollIntoView({ behavior: 'smooth', block: 'center' })
    this.renderPosition()
  }

  renderPosition() {
    const query = this.queryTarget.value.trim()
    if (query.length < MIN_QUERY) this.positionTarget.textContent = ''
    else if (!this.matches.length) this.positionTarget.textContent = 'No matches'
    else this.positionTarget.textContent = `${this.index + 1} of ${this.matches.length}`
  }

  applyMatchesOnly() {
    const filtering = this.matchesOnly && this.queryTarget.value.trim().length >= MIN_QUERY
    this.turnTargets.forEach((turn) => turn.classList.toggle('d-none', filtering && !turn.querySelector('mark')))
    this.dividerTargets.forEach((divider) => divider.classList.toggle('d-none', filtering))
  }

  // Wraps each occurrence in a <mark> built from text nodes, so transcript text
  // is never parsed as HTML.
  mark(element, query) {
    const text = element.textContent
    const lower = text.toLowerCase()
    let from = 0
    let at = lower.indexOf(query)
    if (at === -1) return

    const fragment = document.createDocumentFragment()
    while (at !== -1) {
      fragment.append(text.slice(from, at))
      const mark = document.createElement('mark')
      mark.className = 'p-0 rounded-1'
      mark.textContent = text.slice(at, at + query.length)
      fragment.append(mark)
      this.matches.push(mark)
      from = at + query.length
      at = lower.indexOf(query, from)
    }
    fragment.append(text.slice(from))
    element.replaceChildren(fragment)
  }

  clearMarks() {
    this.matches = []
    this.textTargets.forEach((text) => {
      if (text.querySelector('mark')) text.replaceChildren(text.textContent)
    })
  }
}
